/**
 * Module loader — registers local frontend shells for modules enabled on
 * the current connection (and/or reported as installed by Marketplace).
 */

import {
  isModuleInstalled,
  registerModule,
  unregisterModule,
} from './index';
import { localFrontends } from '@/modules/localFrontends';
import { logger } from '@/utils/logger';
import { TokenManager } from '@/utils/api';

function normaliseModuleName(raw: string): string {
  return raw.toLowerCase().replace(/[\s-]+/g, '_');
}

async function registerLocal(moduleId: string): Promise<void> {
  const entry = localFrontends[moduleId];
  if (!entry || isModuleInstalled(moduleId)) return;
  const mod = await entry.load();
  registerModule({
    id: moduleId,
    name: entry.name,
    version: entry.version,
    path: entry.path,
    component: mod.default,
  });
}

/**
 * Register local frontends whose ids appear in `enabledIds`.
 */
export async function loadModulesForConnection(
  enabledIds: string[] | null | undefined,
): Promise<void> {
  if (!enabledIds?.length) return;
  const normalised = new Set(enabledIds.map(normaliseModuleName));
  await Promise.all(
    Object.keys(localFrontends)
      .filter((id) => normalised.has(id))
      .map((id) => registerLocal(id)),
  );
}

/**
 * Also pick up Marketplace-installed modules that have a local frontend.
 */
export async function loadModulesFromAPI(): Promise<void> {
  const token = TokenManager.getAccessToken();
  if (!token) return;

  try {
    const response = await fetch('/api/marketplace/installed', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      logger.error('Failed to fetch installed modules:', response.statusText);
      return;
    }

    const items: unknown = await response.json();
    if (!Array.isArray(items)) return;

    // Real `/marketplace/installed` shape (InstalledModule): module_key /
    // display_name / is_active — not the pre-2026-04-10 nested `module.name`.
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const row = item as {
        is_active?: boolean;
        module_key?: string;
        display_name?: string;
        installed_version?: string;
      };
      if (!row.is_active) continue;
      const rawKey = row.module_key ?? '';
      const moduleId = normaliseModuleName(rawKey);
      if (!(moduleId in localFrontends)) {
        logger.log(
          `No local frontend for marketplace module "${row.display_name ?? rawKey}" (${moduleId})`,
        );
        continue;
      }
      await registerLocal(moduleId);
    }
  } catch (error) {
    logger.error('Failed to load modules from API:', error);
  }
}

export async function unloadModule(moduleId: string): Promise<void> {
  unregisterModule(moduleId);
  logger.log(`Module ${moduleId} unloaded`);
}

/**
 * Entry point — call after auth + connection selection.
 */
export async function initializeModules(
  enabledIds?: string[] | null,
): Promise<void> {
  await loadModulesForConnection(enabledIds);
  await loadModulesFromAPI();
}
